import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import { Input, Select, RadioButton } from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function OHF019() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [dMedName, setDMedName] = useState("");
  const [dMedDosage, setDMedDosage] = useState("");
  const [dMedFrequency, setDMedFrequency] = useState("");
  const [dMedStartDate, setDMedStartDate] = useState("");
  const [dMedEndDate, setDMedEndDate] = useState("");

  const [nMedName, setNMedName] = useState("");
  const [nMedDosage, setNMedDosage] = useState("");
  const [nMedFrequency, setNMedFrequency] = useState("");
  const [nMedStartDate, setNMedStartDate] = useState("");
  const [nMedEndDate, setNMedEndDate] = useState("");

  const [oMedName, setOMedName] = useState("");
  const [oMedDosage, setOMedDosage] = useState("");
  const [oMedFrequency, setOMedFrequency] = useState("");
  const [oMedStartDate, setOMedStartDate] = useState("");
  const [oMedEndDate, setOMedEndDate] = useState("");

  const handleAddDiscontinuedMed = () => {
    let discontinuedMedicationsData;

    if (data.DiscontinuedMedications["@value"] !== "")
      discontinuedMedicationsData = [
        ...JSON.parse(data.DiscontinuedMedications["@value"]),
      ];
    else discontinuedMedicationsData = [];

    discontinuedMedicationsData.push({
      Name: dMedName,
      Dosage: dMedDosage,
      Frequency: dMedFrequency,
      StartDate: dMedStartDate,
      EndDate: dMedEndDate,
    });

    const obj = { ...data };
    obj.DiscontinuedMedications["@value"] = JSON.stringify(
      discontinuedMedicationsData
    );
    setData(obj);

    setDMedName("");
    setDMedDosage("");
    setDMedFrequency("");
    setDMedStartDate("");
    setDMedEndDate("");
  };

  const handleAddNewMed = () => {
    let newMedicationsData;

    if (data.NewMedications["@value"] !== "")
      newMedicationsData = [...JSON.parse(data.NewMedications["@value"])];
    else newMedicationsData = [];

    newMedicationsData.push({
      Name: nMedName,
      Dosage: nMedDosage,
      Frequency: nMedFrequency,
      StartDate: nMedStartDate,
      EndDate: nMedEndDate,
    });

    const obj = { ...data };
    obj.NewMedications["@value"] = JSON.stringify(newMedicationsData);
    setData(obj);

    setNMedName("");
    setNMedDosage("");
    setNMedFrequency("");
    setNMedStartDate("");
    setNMedEndDate("");
  };

  const handleAddOngoingMed = () => {
    let ongoingMedicationsData;

    if (data.NewMedications["@value"] !== "")
      ongoingMedicationsData = [...JSON.parse(data.NewMedications["@value"])];
    else ongoingMedicationsData = [];

    ongoingMedicationsData.push({
      Name: oMedName,
      Dosage: oMedDosage,
      Frequency: oMedFrequency,
      StartDate: oMedStartDate,
      EndDate: oMedEndDate,
    });

    const obj = { ...data };
    obj.OngoingMedications["@value"] = JSON.stringify(ongoingMedicationsData);
    setData(obj);

    setOMedName("");
    setOMedDosage("");
    setOMedFrequency("");
    setOMedStartDate("");
    setOMedEndDate("");
  };

  const approvalStatusOptions = [
    { _id: "Approved", name: "Approved" },
    { _id: "Not Approved", name: "Not Approved" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <p>
                <i>
                  INSTRUCTIONS: This form is completed and faxed to the
                  Custodial agency for approval when medications are changed, as
                  required by county contract. Verbal approval must be given by
                  the Custodial Agency prior to the prescription being filled.
                </i>
              </p>
              <div className="row">
                <div className="col">
                  <Input
                    name="Height"
                    label="Height:"
                    error={errors.Height}
                    value={data.Height["@value"]}
                    onChange={handleChange}
                    identical={compare("Height")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="Weight"
                    label="Weight:"
                    error={errors.Weight}
                    value={data.Weight["@value"]}
                    onChange={handleChange}
                    identical={compare("Weight")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <Input
                    name="CustodialAgency"
                    label="Custodial Agency:"
                    error={errors.CustodialAgency}
                    value={data.CustodialAgency["@value"]}
                    onChange={handleChange}
                    identical={compare("CustodialAgency")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="PrescribingDoctor"
                    label="Prescribing Doctor:"
                    error={errors.PrescribingDoctor}
                    value={data.PrescribingDoctor["@value"]}
                    onChange={handleChange}
                    identical={compare("PrescribingDoctor")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="PrescribingAgency"
                    label="Agency/Clinic Name:"
                    error={errors.PrescribingAgency}
                    value={data.PrescribingAgency["@value"]}
                    onChange={handleChange}
                    identical={compare("PrescribingAgency")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <Input
                name="Reason"
                label="Reason for the change:"
                error={errors.Reason}
                value={data.Reason["@value"]}
                onChange={handleChange}
                identical={compare("Reason")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Discontinued Medication(s)</th>
                    <th>Dosage</th>
                    <th>Frequency</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.DiscontinuedMedications["@value"] &&
                    JSON.parse(data.DiscontinuedMedications["@value"]).map(
                      (med, index) => (
                        <tr key={med._id}>
                          <td>{med.Name}</td>
                          <td>{med.Dosage}</td>
                          <td>{med.Frequency}</td>
                          <td>
                            {med.StartDate !== ""
                              ? moment(med.StartDate).format("L")
                              : ""}
                          </td>
                          <td>
                            {med.EndDate !== ""
                              ? moment(med.EndDate).format("L")
                              : ""}
                          </td>
                        </tr>
                      )
                    )}
                  <tr>
                    <td>
                      <Input
                        name="DMedName"
                        value={dMedName}
                        onChange={(e) => setDMedName(e.currentTarget.value)}
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="DMedDosage"
                        value={dMedDosage}
                        onChange={(e) => setDMedDosage(e.currentTarget.value)}
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="DMedFrequency"
                        value={dMedFrequency}
                        onChange={(e) =>
                          setDMedFrequency(e.currentTarget.value)
                        }
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="DMedStartDate"
                        type="date"
                        value={dMedStartDate}
                        onChange={(e) =>
                          setDMedStartDate(e.currentTarget.value)
                        }
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="DMedEndDate"
                        type="date"
                        value={dMedEndDate}
                        onChange={(e) => setDMedEndDate(e.currentTarget.value)}
                        disabled={formDisabled}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <button
                className="btn btn-primary"
                onClick={handleAddDiscontinuedMed}
                disabled={
                  !dMedName ||
                  !dMedDosage ||
                  !dMedFrequency ||
                  !dMedStartDate ||
                  !dMedEndDate
                }
              >
                Add
              </button>
              <br />
              <br />
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>New Medication(s)</th>
                    <th>Dosage</th>
                    <th>Frequency</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.NewMedications["@value"] &&
                    JSON.parse(data.NewMedications["@value"]).map(
                      (med, index) => (
                        <tr key={med._id}>
                          <td>{med.Name}</td>
                          <td>{med.Dosage}</td>
                          <td>{med.Frequency}</td>
                          <td>
                            {med.StartDate !== ""
                              ? moment(med.StartDate).format("L")
                              : ""}
                          </td>
                          <td>
                            {med.EndDate !== ""
                              ? moment(med.EndDate).format("L")
                              : ""}
                          </td>
                        </tr>
                      )
                    )}
                  <tr>
                    <td>
                      <Input
                        name="NMedName"
                        value={nMedName}
                        onChange={(e) => setNMedName(e.currentTarget.value)}
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="NMedDosage"
                        value={nMedDosage}
                        onChange={(e) => setNMedDosage(e.currentTarget.value)}
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="NMedFrequency"
                        value={nMedFrequency}
                        onChange={(e) =>
                          setNMedFrequency(e.currentTarget.value)
                        }
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="NMedStartDate"
                        type="date"
                        value={nMedStartDate}
                        onChange={(e) =>
                          setNMedStartDate(e.currentTarget.value)
                        }
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="NMedEndDate"
                        type="date"
                        value={nMedEndDate}
                        onChange={(e) => setNMedEndDate(e.currentTarget.value)}
                        disabled={formDisabled}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <button
                className="btn btn-primary"
                onClick={handleAddNewMed}
                disabled={
                  !nMedName ||
                  !nMedDosage ||
                  !nMedFrequency ||
                  !nMedStartDate ||
                  !nMedEndDate
                }
              >
                Add
              </button>
              <br />
              <br />
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Ongoing Medication(s)</th>
                    <th>Dosage</th>
                    <th>Frequency</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.OngoingMedications["@value"] &&
                    JSON.parse(data.OngoingMedications["@value"]).map(
                      (med, index) => (
                        <tr key={med._id}>
                          <td>{med.Name}</td>
                          <td>{med.Dosage}</td>
                          <td>{med.Frequency}</td>
                          <td>
                            {med.StartDate !== ""
                              ? moment(med.StartDate).format("L")
                              : ""}
                          </td>
                          <td>
                            {med.EndDate !== ""
                              ? moment(med.EndDate).format("L")
                              : ""}
                          </td>
                        </tr>
                      )
                    )}
                  <tr>
                    <td>
                      <Input
                        name="OMedName"
                        value={oMedName}
                        onChange={(e) => setOMedName(e.currentTarget.value)}
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="OMedDosage"
                        value={oMedDosage}
                        onChange={(e) => setOMedDosage(e.currentTarget.value)}
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="OMedFrequency"
                        value={oMedFrequency}
                        onChange={(e) =>
                          setOMedFrequency(e.currentTarget.value)
                        }
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="OMedStartDate"
                        type="date"
                        value={oMedStartDate}
                        onChange={(e) =>
                          setOMedStartDate(e.currentTarget.value)
                        }
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <Input
                        name="OMedEndDate"
                        type="date"
                        value={oMedEndDate}
                        onChange={(e) => setOMedEndDate(e.currentTarget.value)}
                        disabled={formDisabled}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <button
                className="btn btn-primary"
                onClick={handleAddOngoingMed}
                disabled={
                  !oMedName ||
                  !oMedDosage ||
                  !oMedFrequency ||
                  !oMedStartDate ||
                  !oMedEndDate
                }
              >
                Add
              </button>
              <br />
              <br />
              <Input
                name="FollowUp"
                label="Date of follow up Pharmacological appointment:"
                type="date"
                error={errors.FollowUp}
                value={data.FollowUp["@value"]}
                onChange={handleChange}
                identical={compare("FollowUp")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="SAFYWorker"
                label="SAFY Worker:"
                error={errors.SAFYWorker}
                value={data.SAFYWorker["@value"]}
                onChange={handleChange}
                identical={compare("SAFYWorker")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row">
                <div className="col">
                  <Input
                    name="ApprovalRepresentative"
                    label="Verbal Approval given by (Custodial Agency Representative):"
                    error={errors.ApprovalRepresentative}
                    value={data.ApprovalRepresentative["@value"]}
                    onChange={handleChange}
                    identical={compare("ApprovalRepresentative")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="ApprovalDate"
                    label="Date:"
                    type="date"
                    error={errors.ApprovalDate}
                    value={data.ApprovalDate["@value"]}
                    onChange={handleChange}
                    identical={compare("ApprovalDate")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <hr
                style={{
                  position: "relative",
                  border: "none",
                  height: "3px",
                  background: "lightgrey",
                }}
              />
              <RadioButton
                name="ApprovalStatus"
                options={approvalStatusOptions}
                error={errors.ApprovalStatus}
                value={data.ApprovalStatus["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="RepresentativeName"
                label="Custodial Agency Representative/Supervisor Name:"
                error={errors.RepresentativeName}
                value={data.RepresentativeName["@value"]}
                onChange={handleChange}
                identical={compare("RepresentativeName")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row">
                <div className="col">
                  <i>Revised Date: 10/14/2019</i>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default OHF019;
